// The linked-device state the whole UI reads: whether this browser is a device, its
// session, and the relay socket opened with it (docs/07 R6: an unlinked browser renders
// metadata only). A discriminated union so a page cannot hold a relay without a session.
import { createContext, useContext } from "react";

import type { Proto } from "../proto/index";
import type { RelayClient } from "../relay/client";
import type { LinkedDevice, Session } from "./link";

/** An enrolled browser waiting for an active device's approval: no session, no keys in use. */
export interface PendingDevice {
  device: LinkedDevice;
  /** Its auth key's fingerprint, for the approver to compare. */
  fingerprint: string;
}

export type DeviceState =
  | { kind: "loading" }
  // A pending browser is still unlinked for every page: it can neither decrypt nor sign.
  | { kind: "unlinked"; error?: string; pending?: PendingDevice }
  | { kind: "linked"; device: LinkedDevice; session: Session; relay: RelayClient; proto: Proto };

export interface DeviceActions {
  /**
   * Generate keys and enrol this browser: with `bootstrapCode` (the one-time device code) as the user's first device
   * (then open a session), otherwise pending until an active device approves it.
   */
  link(name: string, bootstrapCode?: string): Promise<void>;
  /** Forget the local keys and close the socket. Revocation server-side is the phone's job. */
  unlink(): Promise<void>;
}

export type DeviceContextValue = DeviceState & DeviceActions;

export const DeviceContext = createContext<DeviceContextValue | undefined>(undefined);

export function useDevice(): DeviceContextValue {
  const value = useContext(DeviceContext);
  if (!value) throw new Error("useDevice outside DeviceProvider");
  return value;
}

/** The linked state or undefined, for pages that render differently when unlinked. */
export function useLinkedDevice(): Extract<DeviceState, { kind: "linked" }> | undefined {
  const device = useDevice();
  return device.kind === "linked" ? device : undefined;
}
